import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { SHIFT_META, fmtLongDate } from './constants';
import { CheckCircle2, Ban, Star, Trash2 } from 'lucide-react';

const STATUS_CYCLE = ['preferred', 'available', 'unavailable'];

const StatusChip = ({ active, status, onClick, disabled, shiftKey }) => {
  const meta = {
    preferred: { label: 'Preferred', icon: Star, activeCls: 'bg-[#1D9E75] text-white border-[#1D9E75]' },
    available: { label: 'Available', icon: CheckCircle2, activeCls: 'bg-[#3B82C4] text-white border-[#3B82C4]' },
    unavailable: { label: 'Unavailable', icon: Ban, activeCls: 'bg-[#D25454] text-white border-[#D25454]' },
  }[status];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex-1 min-h-[44px] px-2 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
        active ? meta.activeCls : 'bg-background border-border text-foreground/70 hover:border-foreground/40'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      data-testid={`day-editor-${shiftKey}-${status}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </button>
  );
};

export const DayEditorSheet = ({ open, onOpenChange, date, entry, editable, onSave }) => {
  const [allDay, setAllDay] = useState(false);
  const [shifts, setShifts] = useState({});
  const [restriction, setRestriction] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setAllDay(!!entry?.unavailable_all_day);
      setShifts({ ...(entry?.shifts || {}) });
      setRestriction(entry?.restriction || '');
      setNote(entry?.note || '');
    }
  }, [open, entry]);

  const toggleShift = (key, status) => {
    setShifts((prev) => {
      const next = { ...prev };
      if (next[key] === status) delete next[key];
      else next[key] = status;
      return next;
    });
  };

  const setAllAvailable = () => {
    setAllDay(false);
    setShifts({ day: 'available', swing: 'available', evening: 'available', overnight: 'available' });
  };

  const handleAllDay = (checked) => {
    setAllDay(checked);
    if (checked) setShifts({});
  };

  const handleSave = () => {
    onSave(date, {
      unavailable_all_day: allDay,
      shifts: allDay ? {} : shifts,
      restriction: restriction.trim(),
      note: note.trim(),
    });
    onOpenChange(false);
  };

  const handleClear = () => {
    onSave(date, null);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-2xl sm:max-w-lg sm:mx-auto" data-testid="day-editor-sheet">
        <SheetHeader className="text-left">
          <SheetTitle data-testid="day-editor-title">{date ? fmtLongDate(date) : ''}</SheetTitle>
          <SheetDescription>
            {editable ? 'Select the shifts you can work, or mark the whole day unavailable.' : 'Read-only — this submission can no longer be edited.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 border-[#1D9E75] text-[#1D9E75] hover:bg-[#1D9E75]/10"
            onClick={setAllAvailable}
            disabled={!editable}
            data-testid="day-editor-all-available"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Available for all shifts
          </Button>

          <div className="space-y-3">
            {SHIFT_META.map((s) => (
              <div key={s.key} className={`rounded-xl border p-3 ${allDay ? 'opacity-50' : ''}`} data-testid={`day-editor-shift-${s.key}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-sm font-semibold">{s.label}</span>
                  <span className="text-xs text-muted-foreground">{s.time}</span>
                </div>
                <div className="flex gap-2">
                  {STATUS_CYCLE.map((st) => (
                    <StatusChip
                      key={st}
                      status={st}
                      shiftKey={s.key}
                      active={shifts[s.key] === st}
                      disabled={!editable || allDay}
                      onClick={() => toggleShift(s.key, st)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[#D25454]/40 bg-[#FBE1E1]/40 p-3">
            <div>
              <div className="text-sm font-semibold text-[#912F2F]">Not available for the entire day</div>
              <div className="text-xs text-muted-foreground">Clears all shift selections for this date</div>
            </div>
            <Switch checked={allDay} onCheckedChange={handleAllDay} disabled={!editable} data-testid="day-editor-all-day-toggle" />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Time restriction <span className="text-muted-foreground font-normal">(optional — marks the day Limited)</span></label>
            <Input
              value={restriction}
              onChange={(e) => setRestriction(e.target.value)}
              placeholder='e.g. "Available after 4:00 PM"'
              disabled={!editable}
              maxLength={300}
              data-testid="day-editor-restriction"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Note <span className="text-muted-foreground font-normal">(optional)</span></label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything the scheduler should know about this date"
              rows={2}
              disabled={!editable}
              maxLength={300}
              data-testid="day-editor-note"
            />
          </div>
        </div>

        {editable && (
          <div className="flex gap-2 pb-2">
            <Button variant="outline" className="h-11" onClick={handleClear} data-testid="day-editor-clear">
              <Trash2 className="h-4 w-4 mr-1.5" />
              Clear Day
            </Button>
            <Button className="flex-1 h-11" onClick={handleSave} data-testid="day-editor-save">
              Save Day
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
